import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'gatsby'
import { Icon } from '@chakra-ui/icons'
import { useLocation } from '@reach/router'
import { AiFillPushpin } from 'react-icons/ai'
import { Box, Collapse, Flex, Text, Center, Spacer } from '@chakra-ui/react'
import { RiArrowDownSLine, RiArrowRightSLine } from 'react-icons/ri'

import DraggablePinnedRow from './draggable-pinned-row'
import NewSidebarItem from './new-sidebar-item'
import PinPageButton from './pin-page-button'
import SidebarItem from '../sidebar-item'
import type { SidebarItem as SidebarItemData } from '../tabs'
import SidebarItemIcon from '../sidebar-item/icon'
import { ephemeralPinSx, getExpandedOnlyDisplay } from './helpers'
import { getIsActive } from '../sidebar-item/helpers'
import { useClinic } from '~contexts/clinic-context'
import { LocalStorageKeys } from '~config/constants'
import { useLocalStorage } from '~utils/hooks/use-local-storage'
import NewTag from '~components/shared/tags/new-tag'
import SharedStack from '~components/shared/wrappers/shared-stack'
import {
    NewSidebarSection,
    NewSidebarSubgroup,
    ResolvedPinnedPage,
    resolvePinnedItems,
    useNewSidebarContent,
} from './new-tabs'
import { usePinnedPages } from './pinned-pages-provider'

interface Props {
    isPinned: boolean
    submenu: string
    setSubmenu: (value: React.SetStateAction<string>) => void
}

const NewSidebarContent: React.FC<Props> = ({
    isPinned,
    submenu,
    setSubmenu,
}) => {
    const { clinic } = useClinic()
    const { pathname } = useLocation()
    const { sections, leftoverItems } = useNewSidebarContent(clinic?.features)
    // Single-open accordion: at most one section open. `undefined` means auto
    // (follow the active section), `null` means the user closed everything.
    const [openSectionId, setOpenSectionId] = useState<
        string | null | undefined
    >(undefined)

    const activeSectionId = useMemo(() => {
        for (const section of sections) {
            const isActive = flattenSectionPages(section).some(
                page =>
                    !!page.to &&
                    getIsActive(page.to, pathname, undefined, page.otherPaths),
            )
            if (isActive) return section.id
        }
        return null
    }, [sections, pathname])

    const effectiveOpenId =
        openSectionId === undefined
            ? (activeSectionId ?? sections[0]?.id ?? null)
            : openSectionId

    // Single dropdown rule: opening a legacy submenu closes all sections.
    useEffect(() => {
        if (submenu !== 'none') {
            setOpenSectionId(null)
        }
    }, [submenu])

    // Follow navigation: when the active section changes, open it.
    useEffect(() => {
        if (activeSectionId) {
            setOpenSectionId(prev =>
                prev === activeSectionId ? prev : activeSectionId,
            )
        }
    }, [activeSectionId])

    if (!clinic) return null

    const toggleSection = (id: string) => {
        setSubmenu('none')
        setOpenSectionId(prev => {
            const effective =
                prev === undefined
                    ? (activeSectionId ?? sections[0]?.id ?? null)
                    : prev
            return effective === id ? null : id
        })
    }

    // Opening a legacy dropdown (e.g. Settings) closes all sections so only
    // one dropdown is ever open.
    const handleSetSubmenu: Props['setSubmenu'] = value => {
        const next =
            typeof value === 'function'
                ? (value as (prev: string) => string)(submenu)
                : value
        if (next !== 'none') {
            setOpenSectionId(null)
        }
        setSubmenu(value)
    }

    return (
        <>
            <PinnedSectionView
                sections={sections}
                isPinned={isPinned}
                setSubmenu={handleSetSubmenu}
            />
            {sections.map(section => (
                <NewSidebarSectionView
                    key={section.id}
                    section={section}
                    isPinned={isPinned}
                    isOpen={effectiveOpenId === section.id}
                    onToggle={() => toggleSection(section.id)}
                    setSubmenu={handleSetSubmenu}
                />
            ))}
            {leftoverItems.map(item => (
                <SidebarItem
                    key={item.label}
                    isPinned={isPinned}
                    {...item}
                    submenu={submenu}
                    setSubmenu={handleSetSubmenu}
                />
            ))}
        </>
    )
}

export default NewSidebarContent

/**
 * Every pinnable row in a section: top-level pages plus each subgroup's setup
 * page and its pages. Single source of truth for active/pinned checks.
 */
function flattenSectionPages(
    section: NewSidebarSection,
): Pick<
    SidebarItemData,
    'to' | 'label' | 'icon' | 'badge' | 'isNew' | 'otherPaths'
>[] {
    return section.entries.flatMap(entry =>
        entry.kind === 'page'
            ? [entry.page]
            : [
                  {
                      to: entry.subgroup.to,
                      label: entry.subgroup.label,
                      icon: section.icon,
                  },
                  ...entry.subgroup.pages,
              ],
    )
}

/**
 * Collapsible shortcut list at the top of the sidebar. Hidden until the user
 * pins something; rows stay in their home sections too. Order is the pin
 * order, adjustable with drag and drop. Independent from the section
 * accordion (section navigation never yanks it closed); the collapsed state
 * persists across reloads.
 */
const PinnedSectionView: React.FC<{
    sections: NewSidebarSection[]
    isPinned: boolean
    setSubmenu: (value: React.SetStateAction<string>) => void
}> = ({ sections, isPinned, setSubmenu }) => {
    const { pathname } = useLocation()
    const { pinnedPages } = usePinnedPages()
    const [isOpen, setIsOpen] = useLocalStorage<boolean>(
        LocalStorageKeys.pinnedSectionOpen,
        true,
    )

    const items: ResolvedPinnedPage[] = useMemo(
        () => resolvePinnedItems(sections, pinnedPages),
        [sections, pinnedPages],
    )

    if (items.length === 0) return null

    const activePage = items
        .map(({ page }) => page)
        .find(
            page =>
                !!page.to &&
                getIsActive(page.to, pathname, undefined, page.otherPaths),
        )
    const isActive = !!activePage
    const badgeCount = items.reduce(
        (acc, { page }) => acc + (page.badge || 0),
        0,
    )
    const hasNewPages = items.some(({ page }) => page.isNew)

    return (
        <Box
            w="full"
            borderWidth={1}
            borderColor={isActive || isOpen ? 'gray.700' : 'transparent'}
            _hover={{
                borderColor: 'gray.700',
                bg: 'gray.800',
            }}
            transition="all 0.2s"
            bg={isActive || isOpen ? 'gray.800' : 'transparent'}
            rounded="lg"
        >
            <Box cursor="pointer" onClick={() => setIsOpen(!isOpen)}>
                <SharedStack row align="center" spacing={1}>
                    <SidebarItemIcon
                        isActive={isActive}
                        icon={
                            isActive && !isOpen
                                ? activePage?.icon || AiFillPushpin
                                : AiFillPushpin
                        }
                        badgeCount={badgeCount || undefined}
                        showIndicator={!isPinned}
                    />
                    <SharedStack
                        row
                        align="center"
                        spacing={1}
                        minW={0}
                        transition="opacity 0.2s ease-in-out"
                        {...getExpandedOnlyDisplay(isPinned)}
                    >
                        <Text
                            noOfLines={1}
                            minW={0}
                            color={isActive ? 'white' : 'gray.300'}
                            fontWeight={isActive ? 'semibold' : 'normal'}
                            transition="0.2s"
                            fontSize="14px"
                        >
                            {isActive && !isOpen && (
                                <Box
                                    as="span"
                                    fontSize="11px"
                                    fontWeight="normal"
                                    color="gray.300"
                                >
                                    Pinned /{' '}
                                </Box>
                            )}
                            {isActive && !isOpen ? activePage?.label : 'Pinned'}
                        </Text>
                        {hasNewPages && (
                            <NewTag ml={1} colorScheme="brand" flexShrink={0} />
                        )}
                        <Spacer />
                        {!!badgeCount && (
                            <Center
                                bg="brand.500"
                                rounded="full"
                                px={2}
                                flexShrink={0}
                            >
                                <Text
                                    fontSize="9px"
                                    color="gray.900"
                                    fontWeight="semibold"
                                >
                                    {badgeCount > 9 ? '9+' : badgeCount}
                                </Text>
                            </Center>
                        )}
                        <Center w="32px" h="32px" flexShrink={0}>
                            <Icon
                                as={
                                    !isOpen
                                        ? RiArrowRightSLine
                                        : RiArrowDownSLine
                                }
                                boxSize="13px"
                                color="gray.400"
                            />
                        </Center>
                    </SharedStack>
                </SharedStack>
            </Box>
            <Box as={Collapse} w="full" in={isOpen}>
                <SharedStack spacing={1} pt={1}>
                    {items.map((item, index) => (
                        <DraggablePinnedRow
                            key={item.page.to || item.page.label}
                            item={item}
                            index={index}
                            isSidebarPinned={isPinned}
                            setSubmenu={setSubmenu}
                        />
                    ))}
                </SharedStack>
            </Box>
        </Box>
    )
}

const NewSidebarSectionView: React.FC<{
    section: NewSidebarSection
    isPinned: boolean
    isOpen: boolean
    onToggle: () => void
    setSubmenu: (value: React.SetStateAction<string>) => void
}> = ({ section, isPinned, isOpen, onToggle, setSubmenu }) => {
    const { pathname } = useLocation()

    // Label-only sections skip the dropdown chrome entirely: static header
    // plus always-visible pages. The accordion state ignores them (a nominal
    // `isOpen` for a label just leaves every dropdown closed).
    if (section.labelOnly) {
        return (
            <>
                <Box
                    px={2}
                    pt={1}
                    {...getExpandedOnlyDisplay(isPinned)}
                    transition="opacity 0.2s ease-in-out"
                >
                    <Text
                        fontSize="10px"
                        fontWeight="bold"
                        textTransform="uppercase"
                        letterSpacing="wider"
                        color="gray.500"
                        noOfLines={1}
                    >
                        {section.label}
                    </Text>
                </Box>
                <NewSidebarSectionEntries
                    section={section}
                    isPinned={isPinned}
                    setSubmenu={setSubmenu}
                />
            </>
        )
    }

    const flatPages = flattenSectionPages(section)
    const activePage = flatPages.find(
        page =>
            !!page.to &&
            getIsActive(page.to, pathname, undefined, page.otherPaths),
    )
    const isActive = !!activePage
    const badgeCount = flatPages.reduce(
        (acc, page) => acc + (page.badge || 0),
        0,
    )
    const hasNewPages = flatPages.some(page => page.isNew)

    return (
        <Box
            w="full"
            borderWidth={1}
            borderColor={isActive || isOpen ? 'gray.700' : 'transparent'}
            _hover={{
                borderColor: 'gray.700',
                bg: 'gray.800',
            }}
            transition="all 0.2s"
            bg={isActive || isOpen ? 'gray.800' : 'transparent'}
            rounded="lg"
        >
            <Box cursor="pointer" onClick={onToggle}>
                <SharedStack row align="center" spacing={1}>
                    <SidebarItemIcon
                        isActive={isActive}
                        icon={
                            isActive && !isOpen
                                ? activePage?.icon || section.icon
                                : section.icon
                        }
                        badgeCount={badgeCount || undefined}
                        showIndicator={!isPinned}
                    />
                    <SharedStack
                        row
                        align="center"
                        spacing={1}
                        minW={0}
                        transition="opacity 0.2s ease-in-out"
                        {...getExpandedOnlyDisplay(isPinned)}
                    >
                        <Text
                            noOfLines={1}
                            minW={0}
                            color={isActive ? 'white' : 'gray.300'}
                            fontWeight={isActive ? 'semibold' : 'normal'}
                            transition="0.2s"
                            fontSize="14px"
                        >
                            {isActive && !isOpen && (
                                <Box
                                    as="span"
                                    fontSize="11px"
                                    fontWeight="normal"
                                    color="gray.300"
                                >
                                    {section.label} /{' '}
                                </Box>
                            )}
                            {isActive && !isOpen
                                ? activePage?.label
                                : section.label}
                        </Text>
                        {hasNewPages && (
                            <NewTag ml={1} colorScheme="brand" flexShrink={0} />
                        )}
                        <Spacer />
                        {!!badgeCount && (
                            <Center
                                bg="brand.500"
                                rounded="full"
                                px={2}
                                flexShrink={0}
                            >
                                <Text
                                    fontSize="9px"
                                    color="gray.900"
                                    fontWeight="semibold"
                                >
                                    {badgeCount > 9 ? '9+' : badgeCount}
                                </Text>
                            </Center>
                        )}
                        <Center w="32px" h="32px" flexShrink={0}>
                            <Icon
                                as={
                                    !isOpen
                                        ? RiArrowRightSLine
                                        : RiArrowDownSLine
                                }
                                boxSize="13px"
                                color="gray.400"
                            />
                        </Center>
                    </SharedStack>
                </SharedStack>
            </Box>
            <Box as={Collapse} w="full" in={isOpen}>
                <NewSidebarSectionEntries
                    section={section}
                    isPinned={isPinned}
                    setSubmenu={setSubmenu}
                />
            </Box>
        </Box>
    )
}

/**
 * Section page list shared by the dropdown body and label-only sections.
 */
const NewSidebarSectionEntries: React.FC<{
    section: NewSidebarSection
    isPinned: boolean
    setSubmenu: (value: React.SetStateAction<string>) => void
}> = ({ section, isPinned, setSubmenu }) => (
    <SharedStack spacing={1} pt={1}>
        {section.entries.map(entry =>
            entry.kind === 'page' ? (
                <NewSidebarItem
                    key={entry.page.to || entry.page.label}
                    page={entry.page}
                    sectionLabel={section.label}
                    isSidebarPinned={isPinned}
                    isSubPage
                    setSubmenu={setSubmenu}
                />
            ) : (
                <NewSidebarSubgroupView
                    key={entry.subgroup.label}
                    subgroup={entry.subgroup}
                    sectionLabel={section.label}
                    isPinned={isPinned}
                    setSubmenu={setSubmenu}
                />
            ),
        )}
    </SharedStack>
)

const NewSidebarSubgroupView: React.FC<{
    subgroup: NewSidebarSubgroup
    sectionLabel: string
    isPinned: boolean
    setSubmenu: (value: React.SetStateAction<string>) => void
}> = ({ subgroup, sectionLabel, isPinned, setSubmenu }) => {
    const { pathname } = useLocation()

    const isActive = getIsActive(subgroup.to, pathname)

    return (
        <>
            <Flex
                align="center"
                gap={1}
                pl={6}
                pr={1}
                py={1}
                sx={ephemeralPinSx}
                transition="opacity 0.2s ease-in-out"
                {...getExpandedOnlyDisplay(isPinned)}
            >
                <Box
                    as={Link}
                    to={subgroup.to}
                    flex={1}
                    minW={0}
                    onClick={() => setSubmenu('none')}
                >
                    <Text
                        fontSize="11px"
                        fontWeight="semibold"
                        color={isActive ? 'white' : 'gray.400'}
                        letterSpacing="wider"
                        textTransform="uppercase"
                        noOfLines={1}
                        minW={0}
                        _hover={{ color: 'white' }}
                    >
                        {subgroup.label}
                    </Text>
                </Box>
                <Box className="sidebar-pin" flexShrink={0}>
                    <PinPageButton
                        to={subgroup.to}
                        label={subgroup.label}
                        sectionLabel={sectionLabel}
                    />
                </Box>
            </Flex>
            {subgroup.pages.map(page => (
                <NewSidebarItem
                    key={page.to || page.label}
                    page={page}
                    sectionLabel={sectionLabel}
                    isSidebarPinned={isPinned}
                    isSubPage
                    setSubmenu={setSubmenu}
                />
            ))}
        </>
    )
}
